import { useEffect, useState } from "react";
import { Dog, IdCard, PawPrint, Scale } from "lucide-react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { ActionBar, Button, Field, SelectField } from "@fawkes/ui";

import { ComboBox } from "./ComboBox";

// De tegel Dier op het dossier, zoals de mockup hem tekent: identiteit (naam, soort, chip), ras en
// geslacht (met kruising), leeftijd en gewicht (geboortedatum, gewicht, overlijden, en de
// gewichtsklasse die eruit volgt of die je zelf kiest). Soort, ras en kleur komen uit dezelfde
// lijsten als bij het inboeken — vrije tekst waar een lijst bestaat levert gegevens op waar je later
// niet op kunt zoeken.

export interface DierGegevens {
  naam: string | null;
  diersoort: string | null;
  ras: string | null;
  geslacht: string | null;
  kleur: string | null;
  gewichtKg: number | null;
  chipTatoeage: string | null;
  datumOverlijden: string | null;
  geboortedatum: string | null;
  kruising: boolean | null;
  kruisingMet: string | null;
  gewichtsklasseId: string | null;
  gewichtsklasseNaam: string | null;
  gewichtsklasseHandmatig: boolean | null;
}

interface Optie {
  id: number;
  label: string;
}

interface Invoer {
  naam: string;
  diersoort: string;
  ras: string;
  geslacht: string;
  kleur: string;
  gewichtKg: string;
  chipTatoeage: string;
  datumOverlijden: string;
  geboortedatum: string;
  kruising: boolean;
  kruisingMet: string;
  /** Leeg = automatisch uit het gewicht. */
  gewichtsklasseId: string;
}

const vanGegevens = (d: DierGegevens): Invoer => ({
  naam: d.naam ?? "",
  diersoort: d.diersoort ?? "",
  ras: d.ras ?? "",
  geslacht: d.geslacht ?? "",
  kleur: d.kleur ?? "",
  gewichtKg: d.gewichtKg != null ? String(d.gewichtKg) : "",
  chipTatoeage: d.chipTatoeage ?? "",
  datumOverlijden: d.datumOverlijden ?? "",
  geboortedatum: d.geboortedatum ?? "",
  kruising: d.kruising === true,
  kruisingMet: d.kruisingMet ?? "",
  gewichtsklasseId: d.gewichtsklasseHandmatig ? (d.gewichtsklasseId ?? "") : "",
});

/** "12 jaar", "7 maanden" — wat de mockup als leeftijd laat zien, uit de geboortedatum. */
export function leeftijdVan(geboortedatum: string, tot: string | null, t: (k: string, o?: unknown) => string): string {
  const geboren = new Date(geboortedatum + "T00:00:00");
  const eind = tot ? new Date(tot + "T00:00:00") : new Date();
  if (Number.isNaN(geboren.getTime()) || Number.isNaN(eind.getTime()) || eind < geboren) return "";
  let maanden = (eind.getFullYear() - geboren.getFullYear()) * 12 + (eind.getMonth() - geboren.getMonth());
  if (eind.getDate() < geboren.getDate()) maanden -= 1;
  if (maanden >= 12) return t("crematieDetail.dier.jaar", { args: [String(Math.floor(maanden / 12))] });
  return t("crematieDetail.dier.maanden", { args: [String(Math.max(0, maanden))] });
}

export function CrematieDossierDierTegel({
  id,
  gegevens,
  onOpgeslagen,
}: {
  id: string;
  gegevens: DierGegevens;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const [invoer, setInvoer] = useState<Invoer>(() => vanGegevens(gegevens));
  const [notice, setNotice] = useState<"conflict" | "forbidden" | "error" | null>(null);
  const { mutate, isLoading: bezig } = useCustomMutation();

  const sleutel = JSON.stringify(gegevens);
  useEffect(() => {
    setInvoer(vanGegevens(gegevens));
    setNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleutel]);

  const soorten = useFeed<Optie>("animals/reference/species");
  const soortId = soorten.find((s) => s.label === invoer.diersoort)?.id ?? null;
  const rassen = useFeed<Optie>(`animals/reference/breeds${soortId != null ? `?species=${soortId}` : ""}`);
  const kleuren = useFeed<Optie>("animals/reference/colours");
  const klassen = useFeed<{ id: string; naam: string }>("crematorium/aanmeldingen/gewichtsklassen");

  const zet = <K extends keyof Invoer>(veld: K, waarde: Invoer[K]) =>
    setInvoer((huidig) => ({ ...huidig, [veld]: waarde }));
  // Een naam die (nog) niet in de lijst staat — vrije tekst van vroeger, of een lijst die nog laadt —
  // blijft zichtbaar als eigen keuze in plaats van stilletjes leeg te lijken.
  const label = (lijst: Optie[], waarde: string) =>
    waarde.startsWith("txt:") ? waarde.slice(4) : (lijst.find((o) => String(o.id) === waarde)?.label ?? "");
  const idVan = (lijst: Optie[], naam: string) => {
    if (naam === "") return "";
    const o = lijst.find((x) => x.label === naam);
    return o ? String(o.id) : "txt:" + naam;
  };
  const opties = (lijst: Optie[], naam: string) => {
    const uit = lijst.map((o) => ({ value: String(o.id), label: o.label }));
    if (naam !== "" && !lijst.some((o) => o.label === naam)) uit.unshift({ value: "txt:" + naam, label: naam });
    return uit;
  };

  const getal = (s: string): number | null => {
    const n = Number(s.replace(",", "."));
    return s.trim() === "" || !Number.isFinite(n) ? null : n;
  };
  const leeg = (s: string): string | null => (s.trim() === "" ? null : s.trim());

  const opslaan = () => {
    setNotice(null);
    mutate(
      {
        url: `crematorium/dossiers/${id}/dier`,
        method: "put",
        values: {
          naam: leeg(invoer.naam),
          diersoort: leeg(invoer.diersoort),
          ras: leeg(invoer.ras),
          geslacht: leeg(invoer.geslacht),
          kleur: leeg(invoer.kleur),
          gewichtKg: getal(invoer.gewichtKg),
          chipTatoeage: leeg(invoer.chipTatoeage),
          datumOverlijden: leeg(invoer.datumOverlijden),
          geboortedatum: leeg(invoer.geboortedatum),
          kruising: invoer.kruising,
          kruisingMet: invoer.kruising ? leeg(invoer.kruisingMet) : null,
          gewichtsklasseId: leeg(invoer.gewichtsklasseId),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onOpgeslagen(),
        onError: (err) => {
          const code = (err as { statusCode?: number }).statusCode;
          setNotice(code === 409 ? "conflict" : code === 403 ? "forbidden" : "error");
        },
      },
    );
  };

  const leeftijd = invoer.geboortedatum ? leeftijdVan(invoer.geboortedatum, invoer.datumOverlijden || null, t) : "";

  return (
    <section className="fui-tile crem-tile crem-tile--dier">
      <header className="fui-tile__header">
        <h4 className="fui-tile__title">
          <span className="fawkes-relatie-tiletitel"><PawPrint size={18} aria-hidden="true" />{t("crematieDetail.section.dier")}</span>
        </h4>
      </header>

      <div className="fawkes-relatie-tile fawkes-relatie-tile--fill">
        {notice && (
          <div className="asiel-tarief__notice asiel-tarief__notice--error">
            {t(`crematoriumTarief.notice.${notice}`)}
          </div>
        )}

        <section className="fawkes-relatie-subcard">
          <h5 className="fawkes-relatie-tile__subhead"><IdCard size={18} aria-hidden="true" />{t("crematieDetail.groep.identiteit")}</h5>
          <div className="fawkes-relatie-grid">
            <Field label={t("crematieDetail.dier.naam")} value={invoer.naam} onChange={(e) => zet("naam", e.target.value)} />
            <ComboBox
              label={t("crematieDetail.dier.diersoort")}
              value={idVan(soorten, invoer.diersoort)}
              onChange={(waarde) => setInvoer((h) => ({ ...h, diersoort: label(soorten, waarde), ras: "", kruisingMet: "" }))}
              options={opties(soorten, invoer.diersoort)}
              placeholder={t("crematieAanmelding.dier.kiezen")}
            />
          </div>
          <div className="fawkes-relatie-grid crem-grid--1">
            <Field label={t("crematieDetail.dier.chip")} value={invoer.chipTatoeage} onChange={(e) => zet("chipTatoeage", e.target.value)} />
          </div>
        </section>

        <section className="fawkes-relatie-subcard">
          <h5 className="fawkes-relatie-tile__subhead"><Dog size={18} aria-hidden="true" />{t("crematieDetail.groep.rasGeslacht")}</h5>
          <div className="fawkes-relatie-grid">
            <ComboBox
              label={t("crematieDetail.dier.ras")}
              value={idVan(rassen, invoer.ras)}
              onChange={(waarde) => zet("ras", label(rassen, waarde))}
              options={opties(rassen, invoer.ras)}
              placeholder={t("crematieAanmelding.dier.kiezen")}
            />
            <SelectField
              label={t("crematieDetail.dier.geslacht")}
              value={invoer.geslacht}
              onChange={(e) => zet("geslacht", e.target.value)}
              options={[
                { value: "", label: "—" },
                { value: "Reu", label: t("crematieAanmelding.dier.reu") },
                { value: "Teef", label: t("crematieAanmelding.dier.teef") },
                { value: "Onbekend", label: t("crematieAanmelding.dier.onbekend") },
              ]}
            />
          </div>
          <label className="crem-check">
            <input type="checkbox" checked={invoer.kruising} onChange={(e) => zet("kruising", e.target.checked)} />
            {" "}{t("crematieDetail.dier.kruising")}
          </label>
          <div className="fawkes-relatie-grid">
            <ComboBox
              label={t("crematieDetail.dier.kruisingMet")}
              value={idVan(rassen, invoer.kruisingMet)}
              onChange={(waarde) => zet("kruisingMet", label(rassen, waarde))}
              options={opties(rassen, invoer.kruisingMet)}
              placeholder="—"
              disabled={!invoer.kruising}
            />
            <ComboBox
              label={t("crematieDetail.dier.kleur")}
              value={idVan(kleuren, invoer.kleur)}
              onChange={(waarde) => zet("kleur", label(kleuren, waarde))}
              options={opties(kleuren, invoer.kleur)}
              placeholder={t("crematieAanmelding.dier.kiezen")}
            />
          </div>
        </section>

        <section className="fawkes-relatie-subcard">
          <h5 className="fawkes-relatie-tile__subhead"><Scale size={18} aria-hidden="true" />{t("crematieDetail.groep.leeftijdGewicht")}</h5>
          <div className="fawkes-relatie-grid">
            <div>
              <Field label={t("crematieDetail.dier.geboortedatum")} type="date" value={invoer.geboortedatum}
                onChange={(e) => zet("geboortedatum", e.target.value)} />
              {leeftijd && <span className="crem-veldhint" data-testid="leeftijd">{t("crematieDetail.dier.leeftijd")}: {leeftijd}</span>}
            </div>
            <Field label={t("crematieDetail.dier.gewicht")} inputMode="decimal" value={invoer.gewichtKg}
              onChange={(e) => zet("gewichtKg", e.target.value)} />
          </div>
          <div className="fawkes-relatie-grid">
            <Field label={t("crematieDetail.dier.overlijden")} type="date" value={invoer.datumOverlijden}
              onChange={(e) => zet("datumOverlijden", e.target.value)} />
            <div>
              <SelectField
                label={t("crematieDetail.crematie.klasse")}
                value={invoer.gewichtsklasseId}
                onChange={(e) => zet("gewichtsklasseId", e.target.value)}
                options={[
                  { value: "", label: t("crematieDetail.dier.klasseAutomatisch", { args: [gegevens.gewichtsklasseNaam ?? "—"] }) },
                  ...klassen.map((k) => ({ value: k.id, label: k.naam })),
                ]}
              />
              <span className="crem-veldhint">{t("crematieDetail.klasse.hint")}</span>
            </div>
          </div>
        </section>
      </div>

      <ActionBar>
        <Button variant="secondary" disabled={bezig} onClick={() => setInvoer(vanGegevens(gegevens))}>
          {t("common.cancel")}
        </Button>
        <Button disabled={bezig} onClick={opslaan}>{t("common.save")}</Button>
      </ActionBar>
    </section>
  );
}

function useFeed<T>(url: string): T[] {
  const { data } = useCustom({ url, method: "get", errorNotification: false });
  const rijen = data?.data as T[] | { content?: T[] } | undefined;
  if (Array.isArray(rijen)) return rijen;
  return (rijen as { content?: T[] } | undefined)?.content ?? [];
}
